import { useTranslator } from "@eo-locale/react";

import { isEmpty } from "lodash";

import Cookies from "js-cookie";

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  FiChevronDown,
  IconButton,
  Wrap,
  Contribution
} from "@/storybook-components";
import PinIcon from "./markers/pin-Icon";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import WrapperComponent from "./wrapper-component";
import {
  setPinDetails,
  setMapCenterCoordinates
} from "@/redux/features/place/slice";
import {
  setView,
  setPrevPageView
} from "@/redux/features/place-right-panel/slice";
import { dateWithSiteLocale } from "@/lib";

import useVotes from "@/hooks/places/use-votes";
import useShowUnverifiedModal from "@/hooks/places/use-unverfied-modal";
import useAccessStatus from "@/hooks/places/use-access-status";
import useUser from "@/hooks/useUser";
import {
  setTriggerPoint,
  setUnverifiedModalOpen
} from "@/redux/features/unverified-modal/slice";

interface ESRIPanelProps {
  viewPinModalProps: any;
  rightPanelProps: any;
}

const ViewPinDrawer = ({
  viewPinModalProps,
  rightPanelProps
}: ESRIPanelProps) => {
  const dispatch = useAppDispatch();
  const { isOpen, onClose } = viewPinModalProps;
  const pinDetails = useAppSelector((state) => state.place.pinDetails);
  const { language } = useTranslator();
  const isAddressVisible = useAppSelector(
    (state) => state.place.place.showAddress
  );
  const closeModal = () => {
    viewPinModalProps.onClose();
    dispatch(setView("activity-feed"));
    dispatch(setPinDetails({}));
    dispatch(setMapCenterCoordinates({}));
  };
  const { showUnverifiedModal } = useShowUnverifiedModal({ isNewPin: false });
  const { accessStatus } = useAccessStatus();
  const { isRegisteredUserLoggedIn, isUnverfiedUserLoggedIn } = useUser();
  const { manageVotes, findCurrentLikedPin } = useVotes();
  const rightPanelView = useAppSelector(
    (state) => state.placesRightPanelView.view
  );
  const projectId = useAppSelector((state) => state.project.project.id);
  const placesId = useAppSelector((state) => state.place.place.id);

  const handleVoteUp = (id) => {
    if (showUnverifiedModal()) return;

    if (accessStatus && !isRegisteredUserLoggedIn && !isUnverfiedUserLoggedIn) {
      const filteredPin = findCurrentLikedPin(id, rightPanelView);
      const triggerPoint = {
        projectId,
        toolId: placesId,
        postId: id,
        triggeredSource: "vote",
        voteStatus: !filteredPin?.voted
      };
      Cookies.set("trigger_point", JSON.stringify(triggerPoint), {
        expires: 1,
        secure: true,
        sameSite: "Strict"
      });
      dispatch(setTriggerPoint(triggerPoint));
      dispatch(setUnverifiedModalOpen(true));
      return;
    }

    manageVotes?.(id, rightPanelView);
  };

  const pin = () => {
    const wrapperPadding = {
      pl: 4,
      pr: 4,
      pt: 10,
      pb: 4
    };
    const {
      id,
      address,
      pinColor,
      pinName,
      userImageUrl,
      createdAt,
      author,
      voted,
      totalVotes,
      pinIcon
    } = pinDetails;
    const pinIconProps = {
      pinColor: pinColor,
      pinIcon: pinIcon,
      pinId: id
    };
    return (
      <Wrap {...wrapperPadding} key={id}>
        <Contribution
          key={id}
          cardVariant="unstyled"
          username={author}
          contributionTime={dateWithSiteLocale(
            createdAt,
            language.toLowerCase()
          )}
          handleOnBodyClick={() => {
            viewPinModalProps.onClose();
            dispatch(setPinDetails(pinDetails));
            dispatch(setPrevPageView("custom-marker"));
            dispatch(setView("view-pin-details"));
            rightPanelProps.onOpen();
          }}
          isAddressVisible={isAddressVisible}
          userAvatarSrc={userImageUrl}
          showFooter={true}
          pinType={pinName || "Pin"}
          pinAddress={address}
          tileBackgroundColor="gray.100"
          isLoaded={!isEmpty(pinDetails)}
          pinColor={pinColor}
          cardHeading=""
          commentsCount={0}
          showCommentIcon={false}
          isThumbsUp={true}
          thumbsUpCount={totalVotes || 0}
          shareText=""
          hasBodyHighlight={false}
          isVoteActive={voted}
          isIconButtonPressed={voted}
          handleVoteUp={() => handleVoteUp(id)}
          pinIcon={<PinIcon {...pinIconProps} />}
        />
      </Wrap>
    );
  };

  return (
    <Drawer
      onOverlayClick={closeModal}
      isOpen={isOpen}
      placement="bottom"
      size="sm"
      onClose={onClose}
    >
      <DrawerContent backgroundColor="gray.100">
        <WrapperComponent
          position={"inherit"}
          right="0"
          top="-27%"
          mb="16px"
          mr="16px"
          display={{ base: "auto", lg: "flex", sm: "flex" }}
        >
          <IconButton
            onClick={closeModal}
            icon={<FiChevronDown />}
            aria-label="activity-close-button"
            size="lg"
            variant="solid"
            borderRadius="50%"
            bg="white"
            color="gray.900"
            boxShadow="lg"
            _hover={{ background: "gray.100", color: "gray.900" }}
            className="activity-feed-button"
          />
        </WrapperComponent>
        <DrawerBody p={0}>{pin()}</DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default ViewPinDrawer;
